// GuardFort.jsx
import React, { useEffect } from "react";
import {
  Shield,
  Lock,
  User,
  ArrowRight,
  Zap,
  CheckCircle,
  Target,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useAnimation } from "framer-motion";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const GuardFort = () => {
  const navigate = useNavigate();
  const controls = useAnimation();

  useEffect(() => {
    window.scrollTo(0, 0);
    controls.start("visible");
  }, [controls]);

  return (
    <div className="text-[#2c2c2c] overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative pt-12 md:pt-16 pb-20 md:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden bg-white/40 border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-6xl mx-auto">
          <motion.div
            className="flex flex-col lg:flex-row items-center justify-between gap-16"
            initial="hidden"
            animate={controls}
            variants={staggerContainer}
          >
            <motion.div className="lg:w-1/2 space-y-6" variants={fadeInUp}>
              <motion.div
                className="inline-flex items-center space-x-2 bg-[#005a9c]/10 px-4 py-1.5 rounded-full border border-[#005a9c]/20"
                variants={fadeInUp}
              >
                <Shield className="w-4 h-4 text-[#005a9c]" />
                <span className="text-[#005a9c] text-sm font-medium tracking-wider uppercase">
                  GUARDFORT
                </span>
              </motion.div>

              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight text-slate-900"
                variants={fadeInUp}
              >
                Advanced Threat Protection for <span className="text-[#005a9c]">AI Applications</span>
              </motion.h1>

              <motion.p
                className="text-lg text-slate-600 leading-relaxed max-w-xl"
                variants={fadeInUp}
              >
                GuardFort provides comprehensive monitoring and protection for AI
                systems, detecting and preventing security threats in real-time.
                Safeguard against prompt injection, data leakage, and adversarial attacks.
              </motion.p>

              <motion.div
                className="flex flex-col sm:flex-row gap-4 pt-2"
                variants={fadeInUp}
              >
                <motion.button
                  onClick={() => navigate("/contactus")}
                  className="group relative bg-[#005a9c] text-white font-bold px-8 py-3.5 rounded-lg overflow-hidden transition-all duration-300 hover:bg-[#003d6b] flex items-center justify-center shadow-lg"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>GET A DEMO</span>
                    <ChevronRight className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" />
                  </span>
                </motion.button>

                <motion.button
                  onClick={() => navigate("/aboutus")}
                  className="flex items-center justify-center px-6 py-3.5 text-[#005a9c] font-medium rounded-lg border-2 border-[#005a9c] hover:bg-[#005a9c]/5 transition-colors"
                  whileHover={{ y: -2 }}
                >
                  <span className="relative z-10 flex items-center">
                    <span>LEARN MORE</span>
                    <Zap className="w-4 h-4 ml-2" />
                  </span>
                </motion.button>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                className="mt-8 flex flex-wrap items-center justify-center sm:justify-start gap-6 opacity-80"
                variants={fadeInUp}
              >
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-slate-600">
                    Enterprise Security
                  </span>
                </div>
                <div className="h-6 w-px bg-slate-300"></div>
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-blue-600" />
                  <span className="text-sm text-slate-600">
                    Real-time Detection
                  </span>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              className="lg:w-1/2 relative"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative z-10 bg-white/80 backdrop-blur-lg rounded-2xl p-8 border border-[#e5eef6] shadow-2xl">
                <div className="relative">
                  <h3 className="text-xl font-bold mb-6 text-slate-900 flex items-center">
                    <Shield className="w-6 h-6 mr-2 text-[#005a9c]" />
                    Security Dashboard
                  </h3>
                  <div className="space-y-6">
                    {[
                      { label: "Threat Detection", value: "98%", color: "bg-green-500" },
                      { label: "Vulnerability Coverage", value: "95%", color: "bg-[#005a9c]" },
                      { label: "Response Time", value: "99.9%", color: "bg-yellow-500" },
                    ].map((item, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm text-slate-600 mb-1.5">
                          <span>{item.label}</span>
                          <span className="font-semibold">{item.value}</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <motion.div
                            className={`h-full ${item.color} rounded-full`}
                            initial={{ width: 0 }}
                            animate={{ width: item.value }}
                            transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                          ></motion.div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">24/7</div>
                        <div className="text-xs text-slate-500">Monitoring</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">99.9%</div>
                        <div className="text-xs text-slate-500">Uptime</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-[#005a9c]">10x</div>
                        <div className="text-xs text-slate-500">Faster</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Section */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="relative z-10 max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center space-x-2 bg-yellow-400/10 px-4 py-1.5 rounded-full border border-yellow-400/20 mb-6">
                <AlertTriangle className="w-4 h-4 text-yellow-600" />
                <span className="text-yellow-600 text-sm font-medium tracking-wider uppercase">Security Essentials</span>
              </div>
              <h2 className="text-4xl font-bold mb-6 text-slate-900">
                Why Advanced Protection is <span className="text-[#005a9c]">Essential</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Generative AI models introduce unique risks that require multi-layered defense mechanisms.
              </p>
            </motion.div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Prompt Injection",
                description:
                  "Attackers manipulate model outputs via malicious inputs, gaining unauthorized control over AI behavior.",
                icon: <Zap className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Prompt Leaking",
                description:
                  "Manipulation techniques that force models to reveal their underlying system instructions and prompts.",
                icon: <Lock className="w-8 h-8 text-[#005a9c]" />,
              },
              {
                title: "Data Leakages",
                description:
                  "Unintentional exposure of training data or sensitive user information during model interaction.",
                icon: <Shield className="w-8 h-8 text-[#005a9c]" />,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="rf-card p-8 text-center"
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center mb-6 mx-auto border border-slate-100">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="relative py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-transparent border-b border-[#e5eef6]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-24">
            {[
              {
                title: "Advanced Detection",
                description:
                  "I/O scanners protect LLMs from data leakage, prompt injection, and emerging security threats.",
                icon: <Shield className="w-6 h-6 text-[#005a9c]" />,
              },
              {
                title: "AI Model-Agnostic",
                description:
                  "Deploy on any LLM (GPT, Llama, Mistral) across Azure OpenAI, Bedrock, and Langchain.",
                icon: <Zap className="w-6 h-6 text-[#005a9c]" />,
              },
              {
                title: "Effortless Deployment",
                description:
                  "Integrate within minutes via SaaS or containers. Includes comprehensive documentation.",
                icon: <CheckCircle className="w-6 h-6 text-[#005a9c]" />,
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="rf-card p-10 group"
                whileHover={{ y: -5 }}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-6 border border-slate-100 group-hover:bg-[#005a9c]/10 transition-colors">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-slate-900 group-hover:text-[#005a9c] transition-colors">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">{item.description}</p>
                <div className="flex items-center text-sm font-semibold text-[#005a9c]">
                  <span>Explore feature</span>
                  <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Secondary Features */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "PII Protection",
                description: "Safeguard sensitive user data with advanced detection and redaction capabilities.",
                icon: <User className="w-5 h-5" />,
              },
              {
                title: "IP Protection",
                description: "Prevent unauthorized leaks of proprietary information and organizational trade secrets.",
                icon: <Lock className="w-5 h-5" />,
              },
              {
                title: "Adversarial Defense",
                description: "Defend against sophisticated prompt injection and complex jailbreak attempts.",
                icon: <Shield className="w-5 h-5" />,
              },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4">
                <div className="w-10 h-10 rounded-lg bg-[#005a9c]/10 flex items-center justify-center text-[#005a9c] shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">{item.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integration & Metrics */}
      <section className="py-24 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24 bg-[#f0f7ff] border-b border-[#e5eef6]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Effortless Integration</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Seamlessly connect GuardFort to your existing stack with flexible deployment options.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            <div className="rf-card p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Flexible Deployment</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Choose our SaaS solution for rapid scalability, or opt for on-premise deployment 
                to maintain full control over your data and infrastructure security.
              </p>
            </div>
            <div className="rf-card p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-4">SIEM & SOAR Ready</h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Seamless API integration allows you to connect with your existing security operations, 
                improving visibility and automating response workflows.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { value: "99.9%", label: "System Uptime" },
              { value: "24/7", label: "Active Guard" },
              { value: "1M+", label: "Daily Blocks" },
              { value: "<50ms", label: "Latency" },
            ].map((metric, index) => (
              <div key={index} className="text-center p-8 bg-white border border-slate-100 rounded-2xl shadow-sm">
                <div className="text-3xl font-bold text-[#005a9c] mb-2">{metric.value}</div>
                <p className="text-xs text-slate-500 font-medium uppercase tracking-widest">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 sm:px-8 lg:px-12 bg-[#005a9c] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Secure Your AI Roadmap Today</h2>
          <p className="text-blue-100 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Get enterprise-grade protection for your AI applications with GuardFort's advanced monitoring and detection.
          </p>
          <button
            onClick={() => navigate("/contactus")}
            className="bg-white text-[#005a9c] font-bold px-10 py-4 rounded-lg shadow-xl hover:bg-slate-50 transition-all"
          >
            Request a Consultation
          </button>
        </div>
      </section>
    </div>
  );
};

export default GuardFort;
